import React, { useEffect, useState } from "react";

import {
  Button,
  Card,
  Col,
  DatePicker,
  message,
  Row,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import fileDownload from "js-file-download";

import { DownloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import TableUserInfoItem from "../../Components/TableUserInfoItem/TableUserInfoItem";
import useDebounce from "../../hooks/useDebounce";
import { DEFAULT_DATES_RANGE_OPTIONS } from "../../Utils/constants";
import { getOnboardingData, getOnboardingStats } from "../api";

const OnboardingDetails = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [totalSignupCounts, setTotalSignupCounts] = useState(0);
  const [totalOnboardingFormfillupCounts, setTotalOnboardingFormfillupCounts] =
    useState(0);
  const [totalFreedomPlanPurchaseCount, setTotalFreedomPlanPurchaseCount] =
    useState(0);
  const debouncedSearch = useDebounce({
    value: search,
    callback: () => setPage(1),
  });
  const [tableKey, setTableKey] = useState(Date.now());

  const creatorsTable = () => {
    const columns = [
      {
        title: "Creator",
        dataIndex: "name",
        key: "name",
        render: (_, record) => {
          return (
            <TableUserInfoItem
              name={record?.name}
              profilePic={record?.profilePicUrl}
              hideContact
            />
          );
        },
      },
      {
        title: "Host",
        dataIndex: "host",
        key: "host",
        render: (text) => (
          <Typography.Link href={`https://${text}`}>{text}</Typography.Link>
        ),
      },
      {
        title: "Stage",
        dataIndex: "stage",
        key: "stage",
      },
      {
        title: "Annual Revenue",
        dataIndex: "annualRevenue",
        key: "annualRevenue",
      },
      {
        title: "Referrer",
        dataIndex: "referrer",
        key: "referrer",
      },
      {
        title: "Interested in WL",
        dataIndex: "interestedInWL",
        key: "interestedInWL",
        render: (value) => (
          <Tag
            color={
              (value === "yes" && "success") ||
              (value === "no" && "error") ||
              "default"
            }
          >
            {value}
          </Tag>
        ),
      },
      {
        title: "Instagram",
        dataIndex: "instagram_profile",
        key: "instagram_profile",
        render: (text) =>
          text ? <Typography.Link href={text}>{text}</Typography.Link> : "-",
      },
      {
        title: "YouTube",
        dataIndex: "youtube_profile",
        key: "youtube_profile",
        render: (text) =>
          text ? <Typography.Link href={text}>{text}</Typography.Link> : "-",
      },
    ];

    return (
      <Table
        key={tableKey}
        columns={columns}
        dataSource={loading ? [] : users}
        pagination={{
          current: page,
          total,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        loading={loading}
        rowKey={(record) => record._id}
        locale={{
          emptyText: loading ? "Loading..." : "No users found",
        }}
        scroll={{ x: "auto" }}
      />
    );
  };

  const handleGetWLCreatorsCSV = async () => {
    try {
      const resp = await getOnboardingData(
        debouncedSearch,
        startDate,
        endDate,
        page,
        10,
        true
      );
      fileDownload(resp, `user-onboarding-data-${Date.now()}.csv`);
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    }
  };

  const handleGetOnboardingDataStats = async () => {
    try {
      const resp = await getOnboardingStats(
        startDate,
        endDate,
        debouncedSearch
      );
      console.log(resp);
      if (resp.code === 0) {
        setTotalSignupCounts(resp?.result?.totalSignup);
        setTotalOnboardingFormfillupCounts(resp?.result?.totalOnboarding);
        setTotalFreedomPlanPurchaseCount(
          resp?.result?.totalFreedomPlanPurchase
        );
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const handleGetOnboardingData = async () => {
    setLoading(true);
    try {
      const resp = await getOnboardingData(
        debouncedSearch,
        startDate,
        endDate,
        page,
        10
      );
      if (resp.code === 0) {
        setUsers(resp?.result?.data);
        setTotal(resp?.result?.total);
        setTableKey(Date.now());
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetOnboardingDataStats();
  }, [startDate, endDate, debouncedSearch]);

  useEffect(() => {
    handleGetOnboardingData();
  }, [page, debouncedSearch, startDate, endDate]);

  return (
    <>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col span={8}>
          <Card>
            <Statistic title="Total Signups" value={totalSignupCounts} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Onboarding Forms Submitted"
              value={totalOnboardingFormfillupCounts}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Freedom Plan Purchases"
              value={totalFreedomPlanPurchaseCount}
            />
          </Card>
        </Col>
      </Row>

      <FiltersWrapper>
        <CustomSearchbar
          placeholder="Search by name or host"
          searchQuery={search}
          setSearchQuery={(value) => {
            setSearch(value);
            setPage(1);
          }}
          style={{ maxWidth: 500 }}
        />

        <DatePicker.RangePicker
          size="large"
          ranges={DEFAULT_DATES_RANGE_OPTIONS}
          allowClear
          value={[startDate, endDate]}
          onChange={(values) => {
            setStartDate(values?.[0]);
            setEndDate(values?.[1]);
          }}
          style={{ marginLeft: "auto" }}
        />

        <Button className="siteBtn grayBtn" onClick={handleGetWLCreatorsCSV}>
          <DownloadOutlined />
          Export CSV
        </Button>
      </FiltersWrapper>

      {creatorsTable()}
    </>
  );
};

export default OnboardingDetails;
